import type { BlockWire, LineKind, LineReading, PlanDefinition, PlannedExerciseWire, TargetWire, WeightsReading } from "@weights/plan-reading";
import { isPrepLabel, labelKey, looksLikeLabel, safeReadWeights, type Outcome } from "./lib.js";

/** Where a line of the expanded text came from: one of the definition's own lines, or a line of a definition it names. */
type Origin = { own: number } | { via: number };

/**
 * The definition's text with the definitions it names written in, as the week's reading does it
 * (`expand` in plan-reading's trainingWeek): prep moves to the top under `Prep:`, anything else is
 * written in place. Each expanded line remembers which of the definition's own lines it came from.
 */
export function expandDefinition(label: string, lines: string[], others: readonly PlanDefinition[]): { text: string; origins: Origin[] } {
  const own = labelKey(label);
  const index = new Map<string, PlanDefinition>();
  for (const d of others) {
    const key = labelKey(d.label);
    if (key && key !== own && !index.has(key)) index.set(key, d);
  }
  const resolve = (source: string[], visited: Set<string>): string[] =>
    source.flatMap((line) => {
      const key = labelKey(line);
      const found = index.get(key);
      if (!found || visited.has(key)) return [line];
      return resolve(found.lines, new Set([...visited, key]));
    });
  const prep: { line: string; origin: Origin }[] = [];
  const body: { line: string; origin: Origin }[] = [];
  lines.forEach((line, i) => {
    const key = labelKey(line);
    const found = key === own ? undefined : index.get(key);
    if (!found) {
      body.push({ line, origin: { own: i } });
      return;
    }
    const written = resolve(found.lines, new Set([own, key])).map((l) => ({ line: l, origin: { via: i } as Origin }));
    (isPrepLabel(found.label) ? prep : body).push(...written);
  });
  const all = prep.length ? [{ line: "Prep:", origin: { via: -1 } as Origin }, ...prep, { line: "", origin: { via: -1 } as Origin }, ...body] : body;
  return { text: all.map((x) => x.line).join("\n"), origins: all.map((x) => x.origin) };
}

/** The expanded reading's lines back onto the definition's own: a line naming another definition reads as a block. */
export function ownLines(read: LineReading[], lines: string[], origins: Origin[]): LineReading[] {
  const own = new Map<number, LineReading>();
  const via = new Map<number, string | undefined>();
  read.forEach((reading, i) => {
    const origin = origins[i];
    if (!origin) return;
    if ("own" in origin) {
      if (!own.has(origin.own)) own.set(origin.own, reading);
    } else if (origin.via >= 0 && via.get(origin.via) === undefined) via.set(origin.via, reading.blockId);
  });
  return lines.map((text, index) => {
    const found = own.get(index);
    if (found) return { ...found, index, text, exerciseIds: found.exerciseIds.slice() };
    if (!text.trim()) return { index, text, kind: "blank" as LineKind, exerciseIds: [] };
    const blockId = via.get(index);
    return blockId === undefined ? { index, text, kind: "block" as LineKind, exerciseIds: [] } : { index, text, kind: "block" as LineKind, blockId, exerciseIds: [] };
  });
}

export interface DefinitionRead {
  reading: WeightsReading;
  /** One per line of `lines`, when the reader reports lines; else null. */
  lines: LineReading[] | null;
}

/**
 * One definition read as the week reads it, its lines mapped back to what he typed: the typing preview
 * (§61.10). What the coach picked for a line to log is in the saved reading, not here.
 */
export function readDefinition(label: string, lines: string[], others: readonly PlanDefinition[]): Outcome<DefinitionRead> {
  const { text, origins } = expandDefinition(label, lines, others);
  const read = safeReadWeights(text);
  if (!read.ok) return read;
  const raw = (read.value as { lines?: unknown }).lines;
  const own = Array.isArray(raw) ? ownLines(raw as LineReading[], lines, origins) : null;
  return { ok: true, value: { reading: read.value, lines: own } };
}

// MARK: What each line became, in words

export type LineStatus = "read" | "unread" | "blank";

/** §61.2: what an unread line is to the athlete. */
export const NOT_READ = "Not read: shown as text, not logged";

export interface LineRow {
  index: number;
  text: string;
  kind: LineKind;
  status: LineStatus;
  /** The reading in a phrase: "3 rounds", "10 reps · Goblet lunge · medium". */
  title: string;
  badges: string[];
  /** The definition this line writes in, when it names one. */
  includes?: string;
}

function number(t: TargetWire): string {
  return t.upper !== undefined && t.upper !== t.amount ? `${t.amount}–${t.upper}` : `${t.amount}`;
}

function unitSuffix(t: TargetWire): string {
  return t.unit === "seconds" ? " s" : t.unit === "meters" ? " m" : "";
}

function amount(t: TargetWire): string {
  const n = number(t);
  if (t.unit === "reps") return `${n} ${t.amount === 1 && t.upper === undefined ? "rep" : "reps"}`;
  return n + unitSuffix(t);
}

/** `3 × 3` for groups or a block's sets written on the line itself, else `10 reps`, `30 s`, `20 m`. */
export function exerciseTarget(e: PlannedExerciseWire, setsOnLine?: number): string {
  if (!e.target) return setsOnLine && setsOnLine > 1 ? `${setsOnLine} sets` : "";
  const compact = number(e.target) + unitSuffix(e.target);
  if (e.groups && e.groups > 1) return `${e.groups} × ${compact}`;
  if (setsOnLine && setsOnLine > 1) return `${setsOnLine} × ${compact}`;
  return amount(e.target);
}

/** In the words of the phone card (Reading's `blockTitle`): `Prep · 4 sets`, `3 sets`. */
export function blockPhrase(b: BlockWire): string {
  const sets = b.sets === 1 ? "1 set" : `${b.sets} sets`;
  const noted = b.note ? `${sets} · ${b.note}` : sets;
  return b.isPrep ? `Prep · ${noted}` : noted;
}

const plural = (n: number, one: string, many = one + "s") => `${n} ${n === 1 ? one : many}`;

/**
 * The editor's right-hand column: one row per line of his text, from the reader's own per-line
 * reading. `lines` may be shorter than `text` while the reading catches up; missing rows are blank or unread.
 */
export function lineRows(textLines: string[], read: DefinitionRead, defined?: (label: string) => string | undefined): LineRow[] {
  const blocks = new Map<string, BlockWire>();
  const exercises = new Map<string, PlannedExerciseWire>();
  for (const b of read.reading.plan.blocks) {
    blocks.set(b.id, b);
    for (const e of b.exercises) exercises.set(e.id, e);
  }
  const byIndex = new Map((read.lines ?? []).map((l) => [l.index, l]));
  const blocksWithLine = new Set((read.lines ?? []).filter((l) => l.kind === "block" && l.blockId).map((l) => l.blockId!));
  return textLines.map((text, index) => {
    const blank = !text.trim();
    const found = byIndex.get(index);
    const kind: LineKind = found && found.text === text ? found.kind : blank ? "blank" : "unread";
    const row: LineRow = { index, text, kind, status: kind === "blank" ? "blank" : kind === "unread" ? "unread" : "read", title: "", badges: [] };
    const block = found?.blockId ? blocks.get(found.blockId) : undefined;
    switch (kind) {
      case "blank":
        break;
      case "unread":
        row.title = NOT_READ;
        break;
      case "heading":
        row.title = (block ? block.isPrep : isPrepLabel(text)) ? "Prep" : "Main";
        break;
      case "block": {
        const includes = looksLikeLabel(text) ? defined?.(text) : undefined;
        if (includes) {
          row.includes = includes;
          row.title = block ? `Includes ${includes} · ${blockPhrase(block)}` : `Includes ${includes}`;
        } else row.title = block ? blockPhrase(block) : "Block";
        break;
      }
      case "exercise": {
        const items = (found?.exerciseIds ?? []).map((id) => exercises.get(id)).filter((e): e is PlannedExerciseWire => !!e);
        const setsOnLine = block && !blocksWithLine.has(block.id) && block.exercises.length === 1 ? block.sets : undefined;
        row.title = items
          .map((e) => [exerciseTarget(e, setsOnLine), e.name, e.note].filter(Boolean).join(" · "))
          .join(" + ");
        if (!row.title) row.title = "Exercise";
        if (items.some((e) => e.perSide)) row.badges.push("each side");
        if (items.some((e) => e.isBodyweight)) row.badges.push("bodyweight");
        if (items.some((e) => e.needsCheck)) row.badges.push("check the reading");
        break;
      }
      case "note": {
        const e = exercises.get(found?.exerciseIds[0] ?? "");
        row.title = e ? `↳ note on ${e.name}` : "↳ note";
        break;
      }
    }
    return row;
  });
}

/** Rows as the fallback reading gives them (no per-line reading yet): unread lines by their text. */
export function fallbackRows(textLines: string[], unread: readonly string[]): LineRow[] {
  const missing = new Set(unread.map((l) => l.trim()));
  return textLines.map((text, index) => {
    const blank = !text.trim();
    const isUnread = !blank && missing.has(text.trim());
    return {
      index,
      text,
      kind: blank ? "blank" : isUnread ? "unread" : "exercise",
      status: blank ? "blank" : isUnread ? "unread" : "read",
      title: isUnread ? NOT_READ : "",
      badges: [],
    };
  });
}

export interface ReadSummary {
  total: number;
  unread: number;
  firstUnread: number | null;
}

export function summarize(rows: readonly { status: LineStatus; index: number }[]): ReadSummary {
  const written = rows.filter((r) => r.status !== "blank");
  const unread = written.filter((r) => r.status === "unread");
  return { total: written.length, unread: unread.length, firstUnread: unread[0]?.index ?? null };
}

export function summaryText(s: ReadSummary): string {
  if (s.total === 0) return "Nothing written yet";
  if (s.unread === 0) return s.total === 1 ? "The line is read" : `All ${s.total} lines read`;
  return `${plural(s.unread, "line")} not read`;
}

/** The line the caret is on. */
export function lineAt(text: string, offset: number): number {
  let n = 0;
  for (let i = 0; i < offset && i < text.length; i++) if (text.charCodeAt(i) === 10) n++;
  return n;
}

/** Where line `n` starts in `text`. */
export function offsetOfLine(text: string, n: number): number {
  let line = 0;
  for (let i = 0; i < text.length; i++) {
    if (line === n) return i;
    if (text.charCodeAt(i) === 10) line++;
  }
  return line === n ? text.length : text.length;
}

export function replaceLine(text: string, n: number, line: string): string {
  const lines = text.split("\n");
  if (n < 0 || n >= lines.length) return text;
  lines[n] = line;
  return lines.join("\n");
}
